// MyDocuments.tsx
import React, { useState } from 'react';

interface Document {
  id: number;
  titlu: string;
  descriere: string;
  categorie: string;
  dataPublicare: string;
  numDescarcari: number;
  fisier: string;
  status: 'aprobat' | 'in_asteptare' | 'respins';
}

interface MyDocumentsProps {
  onBack: () => void;
}

const MyDocuments: React.FC<MyDocumentsProps> = ({ onBack }) => {
  // Date exemplu
  const [myDocuments, setMyDocuments] = useState<Document[]>([
    {
      id: 1,
      titlu: "Strategie de marketing pentru IMM-uri",
      descriere: "Ghid complet pentru strategii de marketing adaptate pentru întreprinderile mici și mijlocii din România.",
      categorie: "marketing",
      dataPublicare: "15 Mar 2025",
      numDescarcari: 12,
      fisier: "strategie_marketing_imm.pdf",
      status: 'aprobat'
    },
    {
      id: 2,
      titlu: "Formular deduceri fiscale 2025",
      descriere: "Model de formular actualizat pentru deducerile fiscale disponibile antreprenorilor în anul 2025.",
      categorie: "formulare",
      dataPublicare: "10 Mar 2025",
      numDescarcari: 5,
      fisier: "formular_deduceri_2025.docx",
      status: 'aprobat'
    },
    {
      id: 3,
      titlu: "Analiză sector producție Vrancea 2025",
      descriere: "Raport detaliat despre starea sectorului de producție din județul Vrancea în primul trimestru din 2025.",
      categorie: "rapoarte",
      dataPublicare: "18 Mar 2025",
      numDescarcari: 0,
      fisier: "analiza_productie_vrancea_2025.pdf",
      status: 'in_asteptare'
    },
  ]);

  // Simularea descărcării documentului
  const handleDownload = (doc: Document) => {
    alert(`Simulare descărcare: ${doc.fisier}`);
  };

  // Simularea ștergerii documentului - fără utilizarea directă a funcției globale confirm
  const handleDelete = (docId: number) => {
    // În loc de confirm, poți folosi window.confirm sau implementa un dialog personalizat
    // Aici folosim window.confirm pentru a evita eroarea eslint
    const isConfirmed = window.confirm('Ești sigur că vrei să ștergi acest document?');
    
    if (isConfirmed) {
      setMyDocuments(prevDocs => prevDocs.filter(doc => doc.id !== docId));
      alert('Document șters cu succes!');
    }
  };

  // Obține eticheta de status
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'aprobat':
        return { text: 'Aprobat', color: 'bg-green-100 text-green-800' };
      case 'in_asteptare':
        return { text: 'În așteptare', color: 'bg-yellow-100 text-yellow-800' };
      case 'respins':
        return { text: 'Respins', color: 'bg-red-100 text-red-800' };
      default:
        return { text: status, color: 'bg-gray-100 text-gray-800' };
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b flex items-center">
        <button 
          onClick={onBack}
          className="mr-3 text-gray-600 hover:text-blue-600"
        >
          ← Înapoi
        </button>
        <h1 className="text-xl font-semibold">Documentele mele</h1>
      </div>

      {/* Conținut */}
      <div className="p-4">
        {myDocuments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-5xl mb-4">📄</div>
            <p>Nu ai încărcat încă niciun document.</p>
            <button 
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
              onClick={() => alert('Navigare către încărcare document')}
            >
              Încarcă primul document
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {myDocuments.map((doc) => {
              const statusLabel = getStatusLabel(doc.status);
              
              return (
                <div key={doc.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex flex-col md:flex-row justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h3 className="text-lg font-semibold text-blue-600 mr-2">
                          {doc.titlu}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${statusLabel.color}`}>
                          {statusLabel.text}
                        </span>
                      </div>
                      <p className="text-gray-700 mb-3">{doc.descriere}</p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="px-2 py-1 bg-gray-100 text-xs rounded-md">
                          {doc.categorie}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-xs rounded-md">
                          {doc.numDescarcari} descărcări
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <div className="flex items-center">
                          <span className="text-sm mr-1">📅</span>
                          <span>{doc.dataPublicare}</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 md:mt-0 md:ml-6 flex items-center space-x-2">
                      <button 
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md font-medium flex items-center text-sm"
                        onClick={() => handleDownload(doc)}
                      >
                        <span className="mr-1">⬇️</span>
                        Descarcă
                      </button>
                      <button 
                        className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1.5 rounded-md font-medium flex items-center text-sm"
                        onClick={() => handleDelete(doc.id)}
                      >
                        <span className="mr-1">🗑️</span>
                        Șterge
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyDocuments;